import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_URL } from '../../utils';

interface ProfileSetupData {
  birthDate: string | null;
  height: number | null;
  gender: string | null;
}

interface ProfileSetupProps {
  onComplete: () => void;
}

export const ProfileSetup: React.FC<ProfileSetupProps> = ({ onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [profile, setProfile] = useState<ProfileSetupData>({
    birthDate: null,
    height: null,
    gender: null
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();

  const steps = [
    {
      label: t('profileSetup.step1.title'),
      description: t('profileSetup.step1.description'),
      content: (
        <Box sx={{ mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('profile.birthDate')}
              value={profile.birthDate ? new Date(profile.birthDate) : null}
              onChange={(date) => {
                const formattedDate = date ? date.toISOString().split('T')[0] : null;
                setProfile(prev => ({ ...prev, birthDate: formattedDate }));
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 }
                }
              }}
            />
          </LocalizationProvider>
        </Box>
      )
    },
    {
      label: t('profileSetup.step2.title'),
      description: t('profileSetup.step2.description'),
      content: (
        <Box sx={{ mt: 2 }}>
          <TextField
            label={t('profile.height')}
            type="number"
            value={profile.height ?? ''}
            onChange={(e) => setProfile(prev => ({ 
              ...prev, 
              height: e.target.value ? parseFloat(e.target.value) : null 
            }))}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary' }}>cm</Typography>
            }}
          />
        </Box>
      )
    },
    {
      label: t('profileSetup.step3.title'),
      description: t('profileSetup.step3.description'),
      content: (
        <Box sx={{ mt: 2 }}>
          <TextField
            select
            label={t('profile.gender')}
            value={profile.gender ?? ''}
            onChange={(e) => setProfile(prev => ({ ...prev, gender: e.target.value || null }))}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">{t('profile.gender.other')}</MenuItem>
            <MenuItem value="male">{t('profile.gender.male')}</MenuItem>
            <MenuItem value="female">{t('profile.gender.female')}</MenuItem>
            <MenuItem value="other">{t('profile.gender.other')}</MenuItem>
          </TextField>
        </Box>
      )
    }
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`${API_URL}/user/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birthDate: profile.birthDate,
          height: profile.height,
          gender: profile.gender
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      onComplete();
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(t('profileSetup.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    // Allow users to skip profile setup
    onComplete();
  };

  const isCurrentStepValid = () => {
    switch (activeStep) {
      case 0:
        return profile.birthDate !== null;
      case 1:
        return profile.height !== null && profile.height > 0;
      case 2:
        return profile.gender !== null;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <Box 
            sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              backgroundColor: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}
          >
            <PersonIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('profileSetup.title')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('profileSetup.subtitle')}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="h6">{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2 }}>{step.description}</Typography>
                {step.content}
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={index === steps.length - 1 ? handleComplete : handleNext}
                    disabled={!isCurrentStepValid() || saving}
                    sx={{ mr: 1 }}
                  >
                    {index === steps.length - 1 ? (
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        t('profileSetup.complete')
                      )
                    ) : (
                      t('profileSetup.next')
                    )}
                  </Button>
                  {index > 0 && (
                    <Button
                      onClick={handleBack}
                      disabled={saving}
                      sx={{ mr: 1 }}
                    >
                      {t('profileSetup.back')}
                    </Button>
                  )}
                  <Button
                    onClick={handleSkip}
                    disabled={saving}
                    color="secondary"
                  >
                    {t('profileSetup.skip')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <FitnessCenterIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('profileSetup.success.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('profileSetup.success.message')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};